import { assertNever } from '@kofno/piper';
import { action, computed, makeObservable, observable } from 'mobx';
import { error } from '../../../../ErrorHandling';
import {
  getSearchParametersFromUrl,
  getUpdatedSearchParameters,
  getUpdatedSortParameters,
} from '../../../../ListFilter';
import { SearchParameter, SearchParameters } from '../../../../ListFilter/Types';
import { Link } from '../../../../Resource/Types';
import { TPlainTextKey } from '../../../../Translations';
import ModalStore from '../../../Modal/Store';
import GroupOptionsStore from '../GroupOptionsStore/GroupOptionStore';
import { State as GroupOptionsState } from '../GroupOptionsStore/Types';
import {
  GroupParticipantsResource,
  loading,
  loadingMore,
  ParticipantResource,
  ready,
  Ready,
  SortableColumn,
  sorting,
  State,
  updatingQuery,
  updatingSearchParameters,
  waiting,
} from './Types';

class GroupParticipantsStore {
  @observable
  state: State;

  readonly optionsStore = new GroupOptionsStore(this);
  readonly modalStore = new ModalStore();

  constructor(id: number) {
    makeObservable(this);
    this.state = loading(waiting(id, getSearchParametersFromUrl()));
  }

  @action
  ready = (resource: GroupParticipantsResource) => {
    switch (this.state.kind) {
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
        this.state = ready(this.state, resource);
        break;
      case 'waiting':
      case 'ready':
      case 'sorting':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loading = () => {
    switch (this.state.kind) {
      case 'waiting':
      case 'sorting':
      case 'ready':
      case 'updating-search-parameters':
        this.state = loading(this.state);
        break;
      case 'loading':
      case 'updating-query':
      case 'error':
      case 'loading-more':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  error = (msg: TPlainTextKey) => (this.state = error(msg));

  updatedSearchParameters = (searchParameter: SearchParameter, state: Ready) =>
    getUpdatedSearchParameters(
      {
        newSearchParameter: searchParameter,
        searchParameters: state.searchParameters,
      },
      'participants_filter',
    );

  @action
  updatingQuery = (searchParameter: SearchParameter) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
      case 'loading-more':
        break;
      case 'ready':
        const searchParameters = this.updatedSearchParameters(searchParameter, this.state);
        this.state = updatingQuery(searchParameters, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updatingSearchParameters = (searchParameter: SearchParameter) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
      case 'loading-more':
        break;
      case 'ready':
        const searchParameters = this.updatedSearchParameters(searchParameter, this.state);
        this.state = updatingSearchParameters(searchParameters, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  sorting = (sortedColumn: SortableColumn) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
      case 'loading-more':
        break;
      case 'ready':
        const searchParameters = getUpdatedSortParameters(
          { searchParameters: this.state.searchParameters, sortedColumn },
          'participants_filter',
        );
        this.state = sorting(searchParameters, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loadingMore = (page: number) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = loadingMore(this.state, page);
        break;
      case 'waiting':
      case 'sorting':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'loading':
      case 'error':
      case 'loading-more':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  filterRemovedParticipant = (participant: ParticipantResource) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
      case 'loading-more':
        break;
      case 'ready':
        const resource: GroupParticipantsResource = {
          payload: {
            ...this.state.resource.payload,
            allParticipantIds: this.state.resource.payload.allParticipantIds.filter(
              (id) => id !== participant.payload.id,
            ),
            participants: {
              ...this.state.resource.payload.participants,
              payload: {
                participants: this.state.resource.payload.participants.payload.participants.filter(
                  (pr) => pr.payload.id !== participant.payload.id,
                ),
              },
            },
          },
          links: this.state.resource.links,
        };
        this.state = ready(this.state, resource);
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get links(): ReadonlyArray<Link> {
    switch (this.state.kind) {
      case 'ready':
        return this.state.resource.links;
      case 'loading':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'waiting':
      case 'error':
      case 'loading-more':
        return [];
    }
  }

  @computed
  get searchParameters(): SearchParameters {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'loading-more':
        return this.state.searchParameters;
      case 'error':
        return [];
    }
  }

  @computed
  get optionsState(): GroupOptionsState {
    switch (this.state.kind) {
      case 'ready':
        return { kind: 'loading', links: this.state.resource.links };
      case 'waiting':
      case 'loading':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
      case 'loading-more':
        return { kind: 'waiting' };
    }
  }
}

export default GroupParticipantsStore;
